import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Constants } from 'app/components/utils/Constants';
import { Log } from 'app/core/logging/log';
import { CategoryModel, IHash } from 'app/models/category-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';


@Component({
  selector: 'app-admin-category',
  templateUrl: './admin-category.page.html',
  styleUrls: ['./admin-category.page.scss'],
})
export class AdminCategoryPage implements OnInit {
  
  categories: CategoryModel[] = [];
  displayedColumns: string[] = ['id', 'parentId', 'code'];
  dataSource = new MatTableDataSource<CategoryModel>;
  private categoryById = new Map<number, CategoryModel>();


  constructor(
    private dialog: MatDialog,
    private restService: RestService,
    public appService: AppService) { }

  ngOnInit() {
    this.getCategories();
  }

  openNew() {
    this.openDetails();
  }

  openDetails(category?: CategoryModel) {
    let dialogRef = this.dialog.open(CategoryModalContent, {
      panelClass: 'responsive-dialog',
      data: { categories: this.categories, category: category }
    });
    dialogRef.afterClosed().subscribe((data: { updated: boolean }) => data?.updated && this.getCategories());
  }

  getCategories(){
    this.restService.getCategories().subscribe(result => {
      Log.debug('getCategories', result);
      this.categories = result;
      this.categoryById = new Map(result.filter(category => category?.id != null).map(category => [category.id as number, category]));
      this.dataSource = new MatTableDataSource(this.buildCategoryTreeRows(result));
    });
  }

  categoryRowClass = (row: CategoryModel) => ({
    disabled: !row?.enabled,
    'category-row--child': this.getCategoryLevel(row) > 0
  });

  getCategoryLevel(category: CategoryModel): number {
    let level = 0;
    let currentParentId = category?.parentId;
    const visited = new Set<number>();

    while (currentParentId != null && this.categoryById.has(currentParentId) && !visited.has(currentParentId)) {
      visited.add(currentParentId);
      level += 1;
      currentParentId = this.categoryById.get(currentParentId)?.parentId;
    }

    return level;
  }

  getParentCategoryLabel(category: CategoryModel): string {
    if (category?.parentId == null) {
      return 'Raíz';
    }

    const parent = this.categoryById.get(category.parentId);
    if (!parent) {
      return `#${category.parentId}`;
    }

    return `${parent.code} (#${parent.id})`;
  }

  buildIndent(level: number): string {
    return `${Math.max(0, level) * 18}px`;
  }

  private buildCategoryTreeRows(categories: CategoryModel[]): CategoryModel[] {
    const byParent = new Map<number | 'root', CategoryModel[]>();
    const register = (key: number | 'root', category: CategoryModel) => {
      const current = byParent.get(key) || [];
      current.push(category);
      byParent.set(key, current);
    };

    categories.forEach(category => {
      register(category.parentId == null ? 'root' : category.parentId, category);
    });

    const sortByCode = (items: CategoryModel[]) => [...items].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
    const result: CategoryModel[] = [];

    const visit = (parentKey: number | 'root') => {
      sortByCode(byParent.get(parentKey) || []).forEach(category => {
        result.push(category);
        if (category.id != null) {
          visit(category.id);
        }
      });
    };

    visit('root');
    return result;
  }

}


@Component({
  selector: 'app-category-modal-content',
  templateUrl: './category-modal.component.html',
  styleUrls: ['./category-modal.component.scss']
})
export class CategoryModalContent implements OnInit {

  isNew:boolean;
  form!: FormGroup;
  literalkeys:string[] = [];
  imagePreview: string | ArrayBuffer | null = null;
  selectedFile: File | null = null;

  constructor(private fb: FormBuilder,
              private restService: RestService,
              public appService: AppService,
              private dialogRef: MatDialogRef<CategoryModalContent>,
                @Inject(MAT_DIALOG_DATA) public data: { categories: CategoryModel[], category: CategoryModel}) {}


  ngOnInit(){
    this.isNew = this.data?.category == null;
    const category:CategoryModel = this.data?.category ?? {} as CategoryModel;
    this.initForm(category);

    //AppService.langs.forEach(l => this.name[l.name] = this.category.literals[l.name].find(f => f.key.toUpperCase().match('_NAME') != null ));
    //AppService.langs.forEach(l => this.description[l.name] = this.category.literals[l.name].find(f => f.key.toUpperCase().match('_DESCRIPTION') != null ));

    
  }

  initForm(category: CategoryModel): void {
      this.form = this.fb.group({
        id: [category.id ?? null],
        parentId: [category.parentId ?? ''],
        code: [category.code ?? '', Validators.required],
        imageName: [],
        literals: [category.literals],
        enabled: [category.enabled ?? true, Validators.required]
      });
      this.literalkeys = this.getAllLiteralKeys(category.literals);
  }

 
  get literals():IHash{
    return this.form.get('literals').value;
  }
  
  
  getAllLiteralKeys(literals: any): string[] {
    if (!literals) return [];

    const keys = new Set<string>();

    Object.values(literals).forEach((langArray: any) => {
      if (Array.isArray(langArray)) {
        langArray.forEach(item => {
          if (item?.key) {
            keys.add(item.key);
          }
        });
      }
    });

    return Array.from(keys);
  }


  close() {
      this.dialogRef.close();
  }

  confirm() {
    const category:CategoryModel = this.prepareDiscount();
    Log.debug('guardarCategory', {category});
    this.update(category);
  }
  
  update(category:any){
    if(!confirm("Esta seguro de realizar la acción?")) {
      Log.trace("Implement delete functionality here");
      return;
    }

    if (this.form.invalid) return;


    if (this.isNew) {
      this.restService.createCategory(category).subscribe({
        next: res => {
          if(this.selectedFile){
            this.onUpload();
          }else{
            this.dialogRef.close({ updated: true }); 
            this.appService.showSuccessToast(Constants.MSG_SUCCESS);
          }
        }
      });
    } else {
      this.restService.putCategory(category).subscribe({
        next: res => {
          if(this.selectedFile){
            this.onUpload();
          }else{
            this.dialogRef.close({ updated: true }); 
            this.appService.showSuccessToast(Constants.MSG_SUCCESS);
          }
        }
      });
    }

  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {

      this.selectedFile = input.files[0];

      const reader = new FileReader();
      reader.onload = () => this.imagePreview = reader.result;
      reader.readAsDataURL(this.selectedFile);
    }
  }

  onUpload() {
    if (this.form.invalid) return;

    const formData = new FormData();
    formData.append('file',  this.selectedFile); 

    this.restService.imageCategory(this.data.category.id, formData).subscribe({
      next: res => {
          this.dialogRef.close({ updated: true }); 
          this.appService.showSuccessToast(Constants.MSG_SUCCESS);
        }
    });
  }

   delete(): void {
    if (!confirm('¿Está seguro de eliminar este descuento?')) return;
    const id:number = this.form.value.id;
    this.restService.deleteCategory(id).subscribe({
      next: res => {
          this.dialogRef.close({ updated: true }); 
          this.appService.showSuccessToast(Constants.MSG_SUCCESS);
        }
    });
  }

  onLiteralsUpdated(updated: any) {
    let literals = this.form.get('literals').value;
    literals = {
      ...literals,
      ...updated
    };
    this.form.patchValue(literals);
  }

  private prepareDiscount() {
    const value = this.form.value;
    const raw = this.form.getRawValue();
    return {
      ...value,
      related: {
        data: value.relatedData,
        dataType: value.relatedDataType,
        type: value.relatedType,
      },
      expirationDate: raw.expirationDate
      ? (raw.expirationDate instanceof Date
          ? raw.expirationDate.getTime()
          : new Date(raw.expirationDate).getTime())
      : null
    };
  }

  filterLiteral(literals: any, key: string){
    return literals.find(f=> f.key == key);
  }


}
